import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

const DEFAULT_LESSON_ID = "lesson-ch4-mirror-setup";

export default function Lesson() {
  const { lessonId: paramId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = paramId || DEFAULT_LESSON_ID;
  const programmeId = searchParams.get("programme_id") || undefined;
  const withProg = (path) =>
    programmeId ? `${path}?programme_id=${encodeURIComponent(programmeId)}` : path;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [completionFeedback, setCompletionFeedback] = useState("");
  const [completed, setCompleted] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizMsg, setQuizMsg] = useState("");
  const [nextLesson, setNextLesson] = useState(null);
  const [programmeComplete, setProgrammeComplete] = useState(false);
  const [progressionReady, setProgressionReady] = useState(false);
  const [learnerStatus, setLearnerStatus] = useState("not_started");
  const [skipBusy, setSkipBusy] = useState(false);

  useEffect(() => {
    setData(null);
    setError("");
    setCompletionFeedback("");
    setQuizResult(null);
    setAnswers({});
    setQuizMsg("");
    setNextLesson(null);
    setProgrammeComplete(false);
    setProgressionReady(false);
    setLearnerStatus("not_started");
    const progParams = programmeId ? { programme_id: programmeId } : {};
    api.get(`/lessons/${lessonId}`, { params: progParams })
      .then(async (res) => {
        setData(res.data);
        const status = res.data.progress?.status || "not_started";
        const isComplete = status === "completed";
        setCompleted(isComplete);
        setLearnerStatus(status === "needs_retry" ? "needs_retry" : isComplete ? "completed" : "not_started");
        if (res.data.my_feedback) {
          setRating(res.data.my_feedback.rating);
          setComment(res.data.my_feedback.comment || "");
        }
        if (isComplete) {
          try {
            const prog = await api.get("/programme", { params: { _ts: Date.now(), ...progParams } });
            setNextLesson(prog.data.next_lesson || null);
            setProgrammeComplete(Boolean(prog.data.programme_complete));
          } catch {
            setNextLesson(null);
            setProgrammeComplete(false);
          }
        }
        setProgressionReady(true);
      })
      .catch((err) => setError(formatApiError(err) || "Could not load the lesson. Please try again."));
  }, [lessonId, programmeId]);

  const submitQuiz = async (e) => {
    e.preventDefault();
    setQuizMsg("");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/lessons/${lessonId}/quiz/submit`, {
        answers,
        ...(programmeId ? { programme_id: programmeId } : {}),
      });
      setQuizResult(res);
      setQuizMsg(res.message);
      if (res.completion?.status === "completed") {
        setCompleted(true);
        setLearnerStatus("completed");
        setCompletionFeedback(res.completion.educational_feedback || "");
        setNextLesson(res.next_lesson || null);
        setProgrammeComplete(Boolean(res.programme_complete));
        setProgressionReady(true);
      } else {
        setCompleted(false);
        setLearnerStatus(res.learner_status || "needs_retry");
        setNextLesson(res.next_lesson || null);
        setProgrammeComplete(false);
        setProgressionReady(true);
      }
      setData((prev) => ({
        ...prev,
        quiz_progress: res.quiz_progress,
        progress: res.completion?.status === "completed"
          ? { ...(prev?.progress || {}), status: "completed" }
          : prev?.progress,
      }));
    } catch (err) {
      setQuizMsg(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const skipForward = async () => {
    setSkipBusy(true);
    setQuizMsg("");
    try {
      const { data: res } = await api.post("/programme/skip-forward", {
        lesson_id: lessonId,
        ...(programmeId ? { programme_id: programmeId } : {}),
      });
      setLearnerStatus("needs_retry");
      setQuizMsg(res.message);
      if (res.next_lesson_id) {
        navigate(withProg(`/lessons/${res.next_lesson_id}`));
      } else {
        navigate(programmeId ? `/dashboard?programme_id=${encodeURIComponent(programmeId)}` : "/dashboard");
      }
    } catch (err) {
      setQuizMsg(formatApiError(err));
    } finally {
      setSkipBusy(false);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    setFeedbackMsg("");
    try {
      const { data: res } = await api.post(`/lessons/${lessonId}/feedback`, {
        rating: Number(rating),
        comment,
      });
      setFeedbackMsg(res.message);
    } catch (err) {
      setFeedbackMsg(formatApiError(err));
    }
  };

  if (error && !data) return <p role="alert" className="text-destructive" data-testid="lesson-error">{error}</p>;
  if (!data) return <p data-testid="lesson-loading">Loading lesson…</p>;

  const { lesson, content } = data;
  const asset = content?.asset || {};
  const omitVisual =
    asset.display === "omit" ||
    asset.status === "omitted" ||
    asset.display === "placeholder" ||
    asset.status === "pending" ||
    !asset.filename;
  const objectives = content?.learning_objectives || [];
  const steps = content?.setup_sequence || [];
  const errors = content?.common_errors || [];
  const reflection = content?.reflection || [];
  const safety = content?.safety_warnings || [];
  const refs = content?.official_source_references || [];
  const quiz = data.quiz;
  const quizProgress = data.quiz_progress;

  return (
    <article className="space-y-10" data-testid="lesson-page">
      <nav>
        <Link to="/dashboard" className="btn-outline" data-testid="back-to-dashboard">
          ← Back to dashboard
        </Link>
      </nav>

      <header className="space-y-2">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary" data-testid="lesson-title">
          {lesson.title}
        </h1>
      {lesson.programme_context && (
        <p className="text-sm text-muted-foreground" data-testid="programme-context">{lesson.programme_context}</p>
      )}
      </header>

      {content?.purpose && (
        <section className="space-y-3" data-testid="lesson-purpose">
          <h2 className="text-base md:text-lg font-semibold">Purpose</h2>
          {String(content.purpose)
            .split("\n\n")
            .map((p, i) => (
              <p key={i} className="max-w-3xl">
                {p}
              </p>
            ))}
        </section>
      )}

      <section className="space-y-3" data-testid="lesson-objectives">
        <h2 className="text-base md:text-lg font-semibold">Learning objectives</h2>
        {objectives.length ? (
          <ul className="list-disc pl-6 space-y-2">
            {objectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No objectives listed for this lesson.</p>
        )}
      </section>

      {!omitVisual && (
        <figure className="asset-slot" data-testid="lesson-visual">
          <img
            src={`/assets/${asset.filename}`}
            alt={asset.alt || lesson.title}
            className="asset-image"
            data-testid="lesson-asset-image"
          />
        </figure>
      )}

      <section className="space-y-3" data-testid="lesson-setup-sequence">
        <h2 className="text-base md:text-lg font-semibold">Practical method</h2>
        {steps.length ? (
          <ol className="list-decimal pl-6 space-y-2">
            {steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        ) : (
          <p className="text-muted-foreground">No steps listed for this lesson.</p>
        )}
      </section>

      {safety.length > 0 && (
        <section className="space-y-3" data-testid="lesson-safety-warnings">
          <h2 className="text-base md:text-lg font-semibold">Safety warnings</h2>
          <ul className="list-disc pl-6 space-y-2">
            {safety.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3" data-testid="lesson-common-errors">
        <h2 className="text-base md:text-lg font-semibold">Common errors</h2>
        {errors.length ? (
          <ul className="list-disc pl-6 space-y-2">
            {errors.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No common errors listed for this lesson.</p>
        )}
      </section>

      <section className="space-y-3" data-testid="lesson-practical-activity">
        <h2 className="text-base md:text-lg font-semibold">Practical activity</h2>
        {content?.practical_activity ? (
          String(content.practical_activity)
            .split("\n\n")
            .map((p, i) => (
              <p key={i} className="max-w-3xl">
                {p}
              </p>
            ))
        ) : (
          <p className="text-muted-foreground">No practical activity listed.</p>
        )}
      </section>

      <section className="space-y-3" data-testid="lesson-reflection">
        <h2 className="text-base md:text-lg font-semibold">Reflection</h2>
        {reflection.length ? (
          <ul className="list-disc pl-6 space-y-2">
            {reflection.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No reflection questions listed.</p>
        )}
      </section>

      {refs.length > 0 && (
        <section className="space-y-3" data-testid="lesson-official-sources">
          <h2 className="text-base md:text-lg font-semibold">Official source references</h2>
          <ul className="list-disc pl-6 space-y-2">
            {refs.map((r, i) => (
              <li key={i}>
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">
                    {r.title || r.url}
                  </a>
                ) : (
                  r.title
                )}
                {r.publisher ? ` — ${r.publisher}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}

      {quiz && (
        <section className="space-y-4" data-testid="lesson-quiz">
          <h2 className="text-base md:text-lg font-semibold">Knowledge check</h2>
          <p className="text-sm text-muted-foreground">
            Answer every question. Pass mark: {quiz.pass_threshold_percent}%. Unlimited retakes.
          </p>
          {quizProgress && (
            <p className="text-sm" data-testid="quiz-progress-summary">
              Attempts: {quizProgress.attempt_count || 0}. Latest: {quizProgress.latest_score_percent ?? "—"}%.
              Best: {quizProgress.best_score_percent ?? "—"}%.{" "}
              {quizProgress.passed ? "Passed." : "Not yet passed."}
            </p>
          )}
          <form className="space-y-6" onSubmit={submitQuiz}>
            {(quiz.questions || []).map((q, idx) => {
              const result = quizResult?.results?.find((r) => r.question_id === q.question_id);
              const showResult = Boolean(result);
              const boxClass = !showResult
                ? "space-y-2"
                : result.correct
                  ? "space-y-2 quiz-result-correct"
                  : "space-y-2 quiz-result-incorrect";
              return (
                <fieldset
                  key={q.question_id}
                  className={`${boxClass} quiz-question-card`}
                  data-testid={`quiz-question-${q.question_id}`}
                  aria-invalid={showResult && !result.correct ? "true" : undefined}
                >
                  <legend className="font-medium">
                    {idx + 1}. {q.stem}
                    {showResult && (
                      <span className="ml-2 text-sm font-semibold" data-testid={`quiz-status-${q.question_id}`}>
                        {result.correct ? "✓ Correct" : "✗ Incorrect"}
                      </span>
                    )}
                  </legend>
                  {q.image?.url && (
                    <figure className="quiz-image-slot my-3" data-testid={`quiz-image-${q.question_id}`}>
                      <img
                        src={q.image.url}
                        alt={q.image.alt || "Quiz diagram"}
                        className="quiz-question-image"
                        data-testid={`quiz-image-img-${q.question_id}`}
                      />
                      {q.image.description && (
                        <figcaption className="text-sm text-muted-foreground mt-2" data-testid={`quiz-image-desc-${q.question_id}`}>
                          <span className="font-semibold text-foreground">Diagram description: </span>
                          {q.image.description}
                        </figcaption>
                      )}
                    </figure>
                  )}
                  {q.official_source_url && (
                    <p className="text-sm" data-testid={`quiz-official-source-${q.question_id}`}>
                      <a
                        href={q.official_source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2"
                      >
                        View official source
                      </a>
                      <span className="text-muted-foreground">
                        {" "}
                        — {q.official_source_title || "Official New Zealand Road Code"}
                      </span>
                    </p>
                  )}
                  <div className="space-y-2">
                    {(q.options || []).map((opt) => {
                      const isChosen = answers[q.question_id] === opt.id;
                      const isCorrectOpt = showResult && result.correct_option_id === opt.id;
                      const isWrongChosen = showResult && !result.correct && isChosen;
                      let optClass = "";
                      if (isWrongChosen) optClass = "quiz-option-wrong";
                      else if (isCorrectOpt) optClass = "quiz-option-right";
                      return (
                        <label key={opt.id} className={`quiz-option ${isChosen ? "quiz-option-checked" : ""} ${optClass}`}>
                          <input
                            type="radio"
                            name={q.question_id}
                            value={opt.id}
                            checked={isChosen}
                            onChange={() => setAnswers({ ...answers, [q.question_id]: opt.id })}
                            required
                            data-testid={`quiz-option-${q.question_id}-${opt.id}`}
                          />
                          <span>
                            {opt.text}
                            {isWrongChosen ? " (Your answer)" : ""}
                            {isCorrectOpt ? " (Correct answer)" : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {showResult && (
                    <div className="space-y-2 text-sm pl-1" data-testid={`quiz-feedback-${q.question_id}`}>
                      {!result.correct && (
                        <p>
                          <span className="font-semibold">Your answer: </span>
                          {result.chosen_option_text || "—"}
                        </p>
                      )}
                      <p>
                        <span className="font-semibold">Correct answer: </span>
                        {result.correct_option_text || "—"}
                      </p>
                      <p>
                        <span className="font-semibold">Why: </span>
                        {result.explanation}
                      </p>
                      {(result.official_source_title || result.official_source_url) && (
                        <p>
                          <span className="font-semibold">Source: </span>
                          {result.official_source_url ? (
                            <a
                              href={result.official_source_url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline-offset-2 hover:underline"
                            >
                              {result.official_source_title || "Official New Zealand Road Code"}
                            </a>
                          ) : (
                            result.official_source_title
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </fieldset>
              );
            })}
            <button type="submit" className="btn-primary" disabled={busy} data-testid="quiz-submit-button">
              {busy ? "Checking…" : quizResult ? "Retake knowledge check" : "Submit knowledge check"}
            </button>
          </form>
          {quizMsg && <p data-testid="quiz-message">{quizMsg}</p>}
          {quizResult?.attempt && (
            <p className="font-medium" data-testid="quiz-results">
              Score: {quizResult.attempt.score_percent}% ({quizResult.attempt.correct_count}/
              {quizResult.attempt.total_count}) — {quizResult.attempt.passed ? "Pass" : "Not passed"}
            </p>
          )}
        </section>
      )}

      <section className="space-y-4" data-testid="lesson-completion">
        {completed ? (
          <>
            <p className="status-success inline-block px-3 py-2 rounded" data-testid="lesson-completed-banner">
              You have completed this lesson.
            </p>
            {progressionReady && nextLesson?.id ? (
              <button
                type="button"
                className="btn-primary"
                data-testid="continue-next-lesson-button"
                onClick={() => navigate(`/lessons/${nextLesson.id}`)}
              >
                Continue to next lesson
              </button>
            ) : progressionReady ? (
              <Link to="/dashboard" className="btn-primary inline-block" data-testid="continue-next-lesson-button">
                Back to dashboard
              </Link>
            ) : null}
          </>
        ) : learnerStatus === "needs_retry" && quizResult && !quizResult?.attempt?.passed ? (
          <div className="space-y-3" data-testid="needs-retry-actions">
            <p className="status-warning inline-block px-3 py-2 rounded" data-testid="needs-retry-banner">
              Needs retry — this lesson is not complete yet.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="btn-primary"
                data-testid="retake-quiz-button"
                onClick={() => {
                  const el = document.querySelector('[data-testid="quiz-submit-button"]');
                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Retake quiz
              </button>
              <button
                type="button"
                className="btn-outline"
                data-testid="review-lesson-button"
                onClick={() => {
                  const el = document.querySelector('[data-testid="lesson-title"]');
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Review lesson
              </button>
              <button
                type="button"
                className="btn-accent"
                disabled={skipBusy || !nextLesson?.id}
                data-testid="continue-after-fail-button"
                onClick={skipForward}
              >
                {skipBusy ? "Continuing…" : nextLesson?.id ? "Continue to next lesson" : "Back to dashboard"}
              </button>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="skip-forward-note">
              Continuing does not mark this lesson complete and does not change your quiz score.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="completion-gate-note">
            Complete the knowledge check to a pass mark to finish this lesson.
          </p>
        )}
        {completionFeedback && (
          <p className="max-w-3xl" data-testid="completion-feedback">
            {completionFeedback}
          </p>
        )}
      </section>

      {completed && (
        <section className="space-y-3" data-testid="feedback-section">
          <h2 className="text-base md:text-lg font-semibold">Lesson feedback (optional)</h2>
          <form className="space-y-3 max-w-xl" onSubmit={submitFeedback}>
            <label className="block space-y-1">
              <span>How useful was this lesson? (1–5)</span>
              <select
                className="input"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                data-testid="feedback-rating"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span>Comment</span>
              <textarea
                className="input"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid="feedback-comment"
              />
            </label>
            <button type="submit" className="btn-accent" data-testid="feedback-submit">
              Submit feedback
            </button>
            {feedbackMsg && <p data-testid="feedback-message">{feedbackMsg}</p>}
          </form>
        </section>
      )}

      <nav>
        <Link to="/dashboard" className="btn-outline">
          ← Back to dashboard
        </Link>
      </nav>
    </article>
  );
}
